import { useMutation, useQueryClient } from "@tanstack/react-query";

import { logger } from "#app/monitoring/Logger";
import { showErrorToast } from "#app/notifications";
import { reorderQuoteRequestLines } from "#modules/quoteRequestLines/infra/reorderQuoteRequestLines.api";
import {
  applyReorderToQuoteRequestLinesCache,
  revertReorderInQuoteRequestLinesCache,
} from "#modules/quoteRequestLines/view/applyReorderToQuoteRequestLinesCache";
import { queryKeys } from "#shared/queryKeys/queryKeys";

type Props = {
  quoteRequestId: string | undefined;
};

export const useReorderQuoteRequestLines = ({ quoteRequestId }: Props) => {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, ...rest } = useMutation({
    mutationFn: reorderQuoteRequestLines,
    onMutate: ({ orderedIds }) => ({
      previousIndexes: applyReorderToQuoteRequestLinesCache({ queryClient, orderedIds }),
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.quoteRequests.detail(quoteRequestId),
      });
    },
    onError: (error, _variables, context) => {
      if (context) {
        revertReorderInQuoteRequestLinesCache({
          queryClient,
          previousIndexes: context.previousIndexes,
        });
      }
      logger.error(error);
      showErrorToast({
        title: "Une erreur est survenue",
        message: "Le réordonnancement des lignes a échoué. Veuillez réessayer.",
      });
    },
  });

  return {
    reorderQuoteRequestLines: mutate,
    reorderQuoteRequestLinesAsync: mutateAsync,
    ...rest,
  };
};
